import { FC, useState } from "react";
import {
  PackageCheck,
  Archive,
  Printer,
  Check,
  ShoppingBag,
  X,
  Loader2,
} from "lucide-react";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { Colors } from "../../../configs";
import { formatCurrency } from "../../../util";
import useTrayPrinter from "../../../hooks/useTrayPrinter";
import { useTranslation } from "../../../util/translation-provider";
import { formatUSDate, formatUS12HourTime } from "../../../util/common-functions";
import { buildPaymentSummary, isDeferredPayment, remainingStoreCreditBalance } from "../../../util/print-formatting";
import { PrintCopyChoice } from "../../../components/pos/payment-modal/CashChangeModal";
import {
  ITrayPrintCustomizationLine,
  ITrayPrintItem,
  ITrayPrintRequest,
} from "../../../typings/interfaces/print/ticket-print-request";
import { receiptStoreFields } from "../../../util/store-header";
import { secondaryAlpha } from "../../../configs/colors";

export interface IPickupReceiptItem {
  description: string;
  name: string;
  qty: number;
  // Discount- and prior-payment-aware net price for this item (post the
  // bill's pro-rata discount share and any already-paid amount) - this is
  // what actually reconciles with what was charged/is still owed, NOT the
  // raw gross qty*unitPrice+extras total.
  price: number;
  // Raw pre-discount gross total for this item - kept alongside `price`
  // purely so the receipt can show an accurate discount figure instead of a
  // hardcoded 0; never used for the total/balance maths itself.
  grossPrice: number;
  // Base-only price for these pieces (unitPrice * pieces, no upcharge/spot/
  // alteration folded in) - what prints in the main item row now, matching
  // check-in/reprint's own fix (#1146) for the same "main row showed the
  // full combined value, double-counting every addon shown as its own
  // priced '+' line underneath" issue. `price`/`grossPrice` above keep their
  // existing (addon-inclusive) meaning since this receipt's own Subtotal/
  // Discount/Balance sum them across items.
  basePrice: number;
  // Regular price of these pieces when Press Only / Clean Only lowered it,
  // shown struck through (see ITrayPrintItem.strikePrice).
  strikePrice?: number;
  // This item's REAL discount share only (pickupReceiptMath's
  // releasedDiscountShare/remainingDiscountShare) - deliberately NOT
  // `grossPrice - price`, since `price` also subtracts whatever was already
  // paid on an earlier visit. Using grossPrice - price as "discount" made an
  // item paid in full at check-in (zero actual discount) print its entire
  // value as "Discount" at pickup - an alteration/upcharge cost that was
  // simply already paid for looked like it had been discounted away.
  discountShare: number;
  // Free-text note a cashier attached to this specific item.
  note?: string;
  // Position of this item's line on the bill (check-in order). The printed
  // receipt lists items in this order whatever their pickup status.
  lineIndex?: number;
  // Upcharges/spots/damages/release notes/alterations attached to this item,
  // grouped by section, each printed as its own "+"-prefixed line under it.
  customizations?: ITrayPrintCustomizationLine[];
}

export interface IPickupReceiptBillSummary {
  billId: string;
  // Items resolved into THIS pickup transaction - "leaving today".
  leavingItems: IPickupReceiptItem[];
  // Items still on the bill that were not selected this time (and weren't
  // already picked up in an earlier transaction) - "remaining in storage".
  remainingItems: IPickupReceiptItem[];
  // Items (or pieces of a multi-piece line) already handed over on an
  // EARLIER, separate pickup visit - shown for reference so a bill
  // collected across several visits still lists everything on it, with
  // each piece's correct status, rather than an already-collected item
  // silently disappearing from every later visit's receipt. Priced at 0 -
  // already charged/reconciled on that earlier visit's own receipt.
  alreadyPickedUpItems?: IPickupReceiptItem[];
  amountChargedThisTransaction: number;
  // Payment records made against this bill in THIS pickup transaction, one
  // per payment - drives the receipt's payment lines.
  payments?: Array<{
    id?: number;
    paymentType: string;
    amount: number;
    tenderedAmount?: number | null;
    storeCreditBalanceAfter?: number | null;
  }>;
  // Whole-bill figures for the printed totals, which always describe the
  // bill as a whole rather than just what leaves today:
  //   Sub Total (billSubtotal) - Discount (billDiscount)
  //   - Previous Payments (paidBefore) = Total owed before today
  //   - today's payments = Balance Due
  billSubtotal: number;
  billDiscount: number;
  // Everything collected on the bill before this transaction.
  paidBefore: number;
  // The bill's assigned pin tag - carried through so the pickup receipt can
  // print it the same way check-in's receipt does.
  pinTagColor?: string;
  pinTagTags?: string[];
  pinTagAlias?: string;
  // The bill's originally scheduled pickup date - only used to print a "Due
  // Next" row when this is a partial pickup (remainingItems is non-empty).
  scheduledPickupDate?: Date;
  // Bill-level cashier notes, carried through the same way.
  notes?: string[];
}

interface IPickupReceiptModalProps {
  open: boolean;
  onDone: () => void;
  bills: IPickupReceiptBillSummary[];
  customerName?: string;
  customerId?: string;
  // Single-letter customer type (R/B/E) and on-file payment preference
  // (M/C), printed next to `customerName` the same way check-in's does.
  customerStatus?: string;
  paymentStatus?: string;
  // Printed on the ticket the same way check-in's does - a store copy with no
  // cashier on it can't be traced back to whoever handed the garments over.
  cashierName?: string;
  cashierId?: string;
  paidBy?: string;
}

// App's established "pending/deferred" amber - the same accent
// CashChangeModal already uses for its LATER/pay-later highlight, reused
// here for "Remaining in Storage" so both screens speak the same color
// language instead of this file inventing its own amber.
const AMBER = "#D89216";
// App's established green-on-tint text, already used by CashChangeModal's
// own highlight banner (paired with `${Colors.PRIMARY}14` as the tint).
const GREEN_TEXT_ON_TINT = "#1B6B2A";

const PRINT_OPTIONS: { key: PrintCopyChoice; label: string }[] = [
  { key: "both", label: "Print Both" },
  { key: "storeCopy", label: "Store Copy" },
  { key: "receipt", label: "Customer Copy" },
];

// Builds a tray-printer payload for one bill's itemized pickup receipt -
// reuses the existing ITrayPrintRequest shape (see util/ticket-print.ts and
// payment-modal.tsx's buildPrintRequestForBill) rather than inventing a new
// wire format, labelling each line item's section directly in its
// description so the physical receipt stays readable on today's printer
// without requiring any tray-app schema change. `copyType` mirrors
// payment-modal.tsx's own store-copy/customer-copy split - same content,
// just tagged for whichever physical copy the tray app is printing.
interface IPickupPrintContext {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  customerName?: string;
  customerId?: string;
  customerStatus?: string;
  paymentStatus?: string;
  cashierName?: string;
  cashierId?: string;
  paidBy?: string;
}

const buildPickupReceiptPrintRequest = (
  bill: IPickupReceiptBillSummary,
  copyType: "receipt" | "storeCopy",
  context: IPickupPrintContext
): ITrayPrintRequest => {
  const now = new Date();
  const alreadyPickedUpItems = bill.alreadyPickedUpItems ?? [];
  // Only a partial/multi-visit pickup actually needs the "- Leaving Today"/
  // "- Remain in Store"/"- Picked Up" tags to distinguish the groups - a
  // single-visit full pickup has nothing remaining or previously collected,
  // so every item is leaving and the receipt should read like a regular,
  // untagged item list.
  const isPartial =
    bill.remainingItems.length > 0 || alreadyPickedUpItems.length > 0;
  const toPrinted = (item: IPickupReceiptItem, description: string): ITrayPrintItem => ({
    qty: item.qty,
    description,
    price: item.price,
    basePrice: item.basePrice,
    strikePrice: item.strikePrice,
    note: item.note,
    customizations: item.customizations,
  });
  // Items print in the bill's original (check-in) order whatever their
  // status - grouping by status reshuffled the list on every visit of a bill
  // collected in several goes. A line split across visits keeps its pieces
  // together, in visit order: picked up earlier, leaving today, remaining.
  // Already-collected items are shown for reference (price is always 0 here;
  // see IPickupReceiptBillSummary.alreadyPickedUpItems) so a bill picked up
  // across several visits still lists every item on it, each with its
  // correct status.
  const items: ITrayPrintItem[] = [
    ...alreadyPickedUpItems.map((item) => ({
      item, rank: 0, printed: toPrinted(item, `${item.description} - Picked Up`),
    })),
    ...bill.leavingItems.map((item) => ({
      item, rank: 1,
      printed: toPrinted(item, isPartial ? `${item.description} - Leaving Today` : item.description),
    })),
    ...bill.remainingItems.map((item) => ({
      item, rank: 2, printed: toPrinted(item, `${item.description} - Remain in Store`),
    })),
  ]
    .sort(
      (a, b) =>
        (a.item.lineIndex ?? Number.MAX_SAFE_INTEGER) -
          (b.item.lineIndex ?? Number.MAX_SAFE_INTEGER) ||
        a.rank - b.rank
    )
    .map(({ printed }) => printed);
  // Whole-bill totals: Total is the bill after discount, earlier payments
  // come off it (the tray prints them as "Previous Payments" above Total),
  // and Balance Due is what the bill still owes once today's payments are in.
  const total = Math.round((bill.billSubtotal - bill.billDiscount) * 100) / 100;
  const paymentSummary = buildPaymentSummary({
    payments: bill.payments ?? [],
    due: Math.max(0, total - bill.paidBefore),
  });
  // LATER/MONTHLY records are not money collected.
  const paid = (bill.payments ?? [])
    .filter((p) => !isDeferredPayment(p.paymentType))
    .reduce((acc, p) => acc + p.amount, 0);

  return {
    copyType,
    invoice: bill.billId,
    // Every pickup prints from here now, so the heading has to say which kind
    // this was rather than always claiming a partial one.
    department:
      bill.remainingItems.length > 0 ? "PARTIAL PICKUP" : "PICKUP",
    dropOff: `${formatUSDate(now)} · ${formatUS12HourTime(now)}`,
    // This receipt documents an active pickup happening right now, so the
    // top-left row always reads "Picked Up" with the actual time - see
    // Print Issues.pdf #4.
    pickupLabel: "Picked Up",
    pickup: formatUSDate(now),
    pickupAfter: formatUS12HourTime(now),
    // Only a partial pickup (something still remains) needs a "Due Next"
    // row - a full pickup has nothing left to be due.
    dueNext:
      bill.remainingItems.length > 0 && bill.scheduledPickupDate
        ? formatUSDate(bill.scheduledPickupDate)
        : undefined,
    dueNextAfter:
      bill.remainingItems.length > 0 && bill.scheduledPickupDate
        ? formatUS12HourTime(bill.scheduledPickupDate)
        : undefined,
    customer: context.customerName ?? "",
    customerId: context.customerId ?? "",
    customerStatus: context.customerStatus,
    paymentStatus: context.paymentStatus,
    cashier: context.cashierName ?? "",
    cashierId: context.cashierId ?? "",
    ...receiptStoreFields({
      name: context.companyName,
      address: context.companyAddress,
      phone: context.companyPhone,
    }),
    items,
    subtotal: bill.billSubtotal,
    discount: bill.billDiscount,
    total,
    notes: bill.notes,
    paidBy: context.paidBy ?? "",
    paid,
    balance: paymentSummary.balanceDue,
    payments: paymentSummary.payments,
    change: paymentSummary.change,
    balanceDue: paymentSummary.balanceDue,
    previouslyPaid: bill.paidBefore,
    storeCreditBalance: remainingStoreCreditBalance(bill.payments ?? []),
    pinTagColor: bill.pinTagColor,
    pinTagTags: bill.pinTagTags ?? [],
    pinTagAlias: bill.pinTagAlias,
  };
};

const ItemRow: FC<{ item: IPickupReceiptItem }> = ({ item }) => {
  const processing = item.customizations?.find((c) => c.section === "Processing");
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-[length:var(--fs-base)]">
      <div className="min-w-0">
        <div className="truncate font-semibold text-[#4D4D4D]">{item.name}</div>
        {item.description !== item.name && (
          <div className="truncate text-[length:var(--fs-xs)] text-[#4D4D4D]/60">
            {item.description}
          </div>
        )}
        {processing && (
          <div className="truncate text-[length:var(--fs-xs)] font-medium text-[#4D4D4D]/80">
            + {processing.label}
          </div>
        )}
      </div>
      <div className="flex flex-none items-center gap-3">
        <span className="whitespace-nowrap text-[length:var(--fs-xs)] text-[#4D4D4D]/60">
          ×{item.qty}
        </span>
        <span className="flex min-w-[60px] flex-col items-end whitespace-nowrap text-right">
          {item.strikePrice !== undefined && (
            <span className="text-[length:var(--fs-xs)] text-[#4D4D4D]/50 line-through">
              {formatCurrency({ amount: item.strikePrice, prefix: "$" })}
            </span>
          )}
          <span className="font-semibold text-[#4D4D4D]">
            {formatCurrency({ amount: item.price, prefix: "$" })}
          </span>
        </span>
      </div>
    </div>
  );
};

const BillReceiptSection: FC<{ bill: IPickupReceiptBillSummary }> = ({
  bill,
}) => {
  const leavingTotal = bill.leavingItems.reduce((acc, i) => acc + i.price, 0);
  const remainingTotal = bill.remainingItems.reduce(
    (acc, i) => acc + i.price,
    0
  );

  return (
    <div className="mb-4">
      {/* Invoice identity row - same icon-badge + name/sub layout language
          CashChangeModal already uses for its "payment method" row. */}
      <div className="mb-3 flex items-center gap-3">
        <div
          className="flex h-[3.5rem] w-[3.5rem] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${Colors.PRIMARY}21`, color: Colors.PRIMARY }}
        >
          <ShoppingBag size={24} />
        </div>
        <div className="min-w-0">
          <div className="truncate text-[length:var(--fs-md)] font-bold text-[#4D4D4D]">
            Invoice {bill.billId}
          </div>
          <div className="mt-0.5 text-[length:var(--fs-xs)] text-[#4D4D4D]/75">
            {bill.leavingItems.length} piece{bill.leavingItems.length === 1 ? "" : "s"} leaving today
            {" · "}
            {bill.remainingItems.length} piece{bill.remainingItems.length === 1 ? "" : "s"} still in storage
          </div>
        </div>
      </div>

      <div
        className="rounded-xl p-4"
        style={{ backgroundColor: Colors.BACKSHADE_GRAY }}
      >
        {/* divide-[#D9D9D9] mirrors Colors.ACCENT_GRAY - Tailwind's JIT
            needs the literal arbitrary-value class, not an interpolated
            constant, to compile the child-border rule `divide-x` relies on. */}
        <div className="grid grid-cols-2 divide-x divide-[#D9D9D9]">
          <div className="pr-4">
            <div
              className="mb-2 flex items-center gap-1.5 text-[length:var(--fs-xs)] font-bold uppercase tracking-wide"
              style={{ color: Colors.PRIMARY }}
            >
              <PackageCheck size={14} />
              Leaving Today ({bill.leavingItems.length})
            </div>
            {bill.leavingItems.length === 0 ? (
              <div className="text-[length:var(--fs-xs)] text-[#4D4D4D]/50">
                Nothing leaving on this invoice
              </div>
            ) : (
              <div className="divide-y divide-[#D9D9D9]/60">
                {bill.leavingItems.map((item, idx) => (
                  <ItemRow key={`${bill.billId}-leaving-${idx}`} item={item} />
                ))}
              </div>
            )}
            <div
              className="mt-2 flex items-center justify-between border-t pt-2 text-[length:var(--fs-base)] font-bold"
              style={{ borderColor: `${Colors.PRIMARY}33`, color: Colors.PRIMARY }}
            >
              <span>Subtotal</span>
              <span>{formatCurrency({ amount: leavingTotal, prefix: "$" })}</span>
            </div>
          </div>

          <div className="pl-4">
            <div
              className="mb-2 flex items-center gap-1.5 text-[length:var(--fs-xs)] font-bold uppercase tracking-wide"
              style={{ color: AMBER }}
            >
              <Archive size={14} />
              Remain in Store ({bill.remainingItems.length})
            </div>
            {bill.remainingItems.length === 0 ? (
              <div className="text-[length:var(--fs-xs)] text-[#4D4D4D]/50">
                Nothing left in storage on this invoice
              </div>
            ) : (
              <div className="divide-y divide-[#D9D9D9]/60">
                {bill.remainingItems.map((item, idx) => (
                  <ItemRow key={`${bill.billId}-remaining-${idx}`} item={item} />
                ))}
              </div>
            )}
            <div
              className="mt-2 flex items-center justify-between border-t pt-2 text-[length:var(--fs-base)] font-bold"
              style={{ borderColor: `${AMBER}33`, color: AMBER }}
            >
              <span>Still Open</span>
              <span>
                {formatCurrency({ amount: remainingTotal, prefix: "$" })}
              </span>
            </div>
          </div>
        </div>

        <div
          className="mt-3 flex items-center justify-between gap-4 rounded-lg px-3 py-3"
          style={{ backgroundColor: `${Colors.PRIMARY}14` }}
        >
          <span
            className="text-[length:var(--fs-base)] font-bold"
            style={{ color: GREEN_TEXT_ON_TINT }}
          >
            Charged This Visit
          </span>
          <span
            className="text-[length:var(--fs-md)] font-bold"
            style={{ color: GREEN_TEXT_ON_TINT }}
          >
            {formatCurrency({
              amount: bill.amountChargedThisTransaction,
              prefix: "$",
            })}
          </span>
        </div>
      </div>
    </div>
  );
};

// On-screen (and, via the Print button, printed) itemized receipt for a
// partial pickup transaction - separates exactly which items are "leaving
// today" from what's "remaining in storage" per bill, following the same
// two-column picked/remaining convention already used for pickup events in
// invoice-history-modal.tsx. Visual language (success badge, method/detail
// panel, highlight banner, 3-way print-copy picker) mirrors CashChangeModal
// - the app's own established "transaction complete" confirmation pattern -
// rather than a one-off design for this screen.
const PickupReceiptModal: FC<IPickupReceiptModalProps> = ({
  open,
  onDone,
  bills,
  customerName,
  customerId,
  customerStatus,
  paymentStatus,
  cashierName,
  cashierId,
  paidBy,
}) => {
  const { printTicket } = useTrayPrinter();
  const translation = useTranslation();
  const [printingCopy, setPrintingCopy] = useState<PrintCopyChoice | null>(null);
  const [printFeedback, setPrintFeedback] = useState<{
    copy: PrintCopyChoice;
    success: boolean;
    message?: string;
  } | null>(null);

  const handlePrintCopySelection = async (copy: PrintCopyChoice) => {
    if (printingCopy || bills.length === 0) return;

    const context: IPickupPrintContext = {
      companyName: translation.companyName,
      companyAddress: translation.companyAddress,
      companyPhone: translation.companyPhone,
      customerName,
      customerId,
      customerStatus,
      paymentStatus,
      cashierName,
      cashierId,
      paidBy,
    };
    const allRequests = bills.flatMap((bill) => [
      buildPickupReceiptPrintRequest(bill, "storeCopy", context),
      buildPickupReceiptPrintRequest(bill, "receipt", context),
    ]);
    const filtered =
      copy === "both" ? allRequests : allRequests.filter((r) => r.copyType === copy);

    if (filtered.length === 0) return;

    setPrintingCopy(copy);
    setPrintFeedback(null);

    let failures = 0;
    for (const req of filtered) {
      const ok = await printTicket(req);
      if (!ok) failures++;
    }
    setPrintingCopy(null);
    setPrintFeedback({
      copy,
      success: failures === 0,
      message:
        failures === 0
          ? undefined
          : failures === filtered.length
            ? "Could not reach the printer. Make sure Presto Tray is running on this computer."
            : `${failures} of ${filtered.length} copies failed to print.`,
    });
  };

  const totalLeavingPieces = bills.reduce(
    (acc, b) => acc + b.leavingItems.reduce((s, i) => s + i.qty, 0),
    0
  );
  const totalRemainingPieces = bills.reduce(
    (acc, b) => acc + b.remainingItems.reduce((s, i) => s + i.qty, 0),
    0
  );

  return (
    <CustomModal
      title="Pickup Receipt"
      openModal={open}
      onClose={onDone}
      mode="popup"
      hideCloseButton
      width="720px"
      height="auto"
      sx={{ maxHeight: "90vh", overflow: "hidden", borderRadius: "1.5rem" }}
    >
      <div className="flex max-h-[80vh] flex-col">
        <div className="relative pb-1 pt-1">
          <button
            type="button"
            onClick={onDone}
            aria-label="Close"
            className="absolute right-0 top-0 flex h-8 w-8 items-center justify-center rounded-full text-[#7C7C7C] transition hover:bg-[#F3F3F3]"
          >
            <X size={20} />
          </button>

          <h2 className="text-center text-[length:var(--fs-lg)] font-extrabold text-[#4D4D4D]">
            Partial Pickup Complete
          </h2>

          <div className="mt-3 flex justify-center">
            <div
              className="flex h-[3.5rem] w-[3.5rem] items-center justify-center rounded-full text-white"
              style={{ backgroundColor: Colors.PRIMARY }}
            >
              <Check size={28} strokeWidth={2.6} />
            </div>
          </div>
          <div
            className="mt-2 text-center text-[length:var(--fs-md)] font-bold"
            style={{ color: Colors.PRIMARY }}
          >
            Pickup Recorded
          </div>

          <p className="mt-2 text-center text-[length:var(--fs-base)] text-[#4D4D4D]/60">
            {totalLeavingPieces} piece{totalLeavingPieces === 1 ? "" : "s"}{" "}
            leaving today · {totalRemainingPieces} piece
            {totalRemainingPieces === 1 ? "" : "s"} still in storage
          </p>
        </div>

        <div className="mt-3 flex-1 overflow-y-auto px-1 pb-1">
          {bills.map((bill) => (
            <BillReceiptSection key={bill.billId} bill={bill} />
          ))}
        </div>

        <div className="mb-2 text-center text-[length:var(--fs-base)] font-semibold text-[#4D4D4D]">
          Print Receipt
        </div>
        <div className="mb-2 grid grid-cols-3 gap-2">
          {PRINT_OPTIONS.map((opt) => {
            const isThisPrinting = printingCopy === opt.key;
            const disabled = !!printingCopy;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                onClick={() => handlePrintCopySelection(opt.key)}
                className="flex h-[3.25rem] items-center justify-center gap-2 rounded-xl border px-3 text-[length:var(--fs-2xs)] font-bold transition-all duration-150 disabled:cursor-not-allowed enabled:hover:-translate-y-px"
                style={{
                  borderColor: Colors.SECONDARY,
                  color: "white",
                  background: `linear-gradient(180deg, ${Colors.SECONDARY} 0%, #0568A7 100%)`,
                  boxShadow: `0 8px 18px ${secondaryAlpha(0.22)}`,
                  opacity: disabled && !isThisPrinting ? 0.5 : 1,
                }}
              >
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: "rgba(255,255,255,0.16)", color: "white" }}
                >
                  {isThisPrinting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Printer size={16} />
                  )}
                </span>
                <span className="whitespace-nowrap tracking-tight">
                  {isThisPrinting ? "Printing…" : opt.label}
                </span>
              </button>
            );
          })}
        </div>

        {printFeedback && (
          <div
            className={`mb-3 rounded-lg px-3 py-2 text-center text-[length:var(--fs-xs)] font-semibold ${
              printFeedback.success
                ? "bg-brand/10"
                : "bg-[#930000]/10"
            }`}
            style={{ color: printFeedback.success ? GREEN_TEXT_ON_TINT : Colors.BROWN }}
          >
            {printFeedback.success
              ? `${PRINT_OPTIONS.find((o) => o.key === printFeedback.copy)?.label ?? "Receipt"} sent to the printer.`
              : printFeedback.message}
          </div>
        )}

        <button
          type="button"
          onClick={onDone}
          className="h-[3.2rem] w-full rounded-xl text-[length:var(--fs-base)] font-bold text-white transition hover:brightness-95"
          style={{ backgroundColor: Colors.PRIMARY }}
        >
          Done
        </button>
      </div>
    </CustomModal>
  );
};

export default PickupReceiptModal;
